import { createStore } from 'vuex';
import VuexPersistence from 'vuex-persist';
import Api from '@/services/api/Api';

const vuexLocal = new VuexPersistence({
  key: 'travel-mind',
  storage: window.localStorage,
});

export default createStore({
  state: {
    email: null,
    isEmailApproved: false,
    token: null,
    user: null,
    idamExists: false,
    role: null,
    company: null,
    country: null,
    allCompanies: null,
    isLoading: false,
  },
  mutations: {
    SET_EMAIL: (state, payload) => {
      state.email = payload.toLowerCase();
    },
    APPROVE_EMAIL: (state, payload) => {
      state.isEmailApproved = payload;
    },
    SET_TOKEN: (state, payload) => {
      state.token = payload;
    },
    SET_COMPANY: (state, payload) => {
      state.company = payload;
    },
    SET_COUNTRY: (state, payload) => {
      state.country = payload;
    },
    SET_ALL_COMPANIES: (state, payload) => {
      state.allCompanies = payload;
    },
    SET_USER: (state, payload) => {
      state.user = payload;
    },
    SET_IDAM_USER: (state, payload) => {
      state.idamExists = payload;
    },
    SET_ROLE: (state, payload) => {
      state.role = payload;
    },
    SET_LOADING: (state, payload) => {
      state.isLoading = payload;
    },
    UPDATE_USER_COUNTRY(state, newCountry) {
      if (state.user && typeof state.user === 'object') {
        state.user.country = newCountry; // direct update is fine here
      }
      state.country = newCountry;
    },
  },
  actions: {
    SET_EMAIL: (context, email) => {
      context.commit('SET_EMAIL', email);
    },
    AUTHENTICATE: async (context, payload) => {
      try {
        context.commit('SET_TOKEN', payload.token);
        const response = await Api.getUserInformation(payload.email);
        context.commit('SET_EMAIL', payload.email);
        if (response.ok) {
          context.commit('APPROVE_EMAIL', response.ok[0].approved);
          context.commit('SET_ROLE', response.ok[0].user_role);
          context.commit('SET_ALL_COMPANIES', response.ok[0].companies);
          context.commit('SET_USER', response.ok[0].user);
          if (context.state.company == null) {
            context.commit('SET_COMPANY', response.ok[0].companies[0]);
          }
          if (context.state.country == null) {
            context.commit('SET_COUNTRY', response.ok[0].country);
          }
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    FETCH_USER_INFORMATION: async (context, email) => {
      try {
        const response = await Api.getUserInformation(email);
        context.commit('SET_EMAIL', email);
        if (response.ok) {
          context.commit('APPROVE_EMAIL', response.ok[0].approved);
          context.commit('SET_ROLE', response.ok[0].user_role);
          context.commit('SET_ALL_COMPANIES', response.ok[0].companies);
          context.commit('SET_USER', response.ok[0].user);
          if (context.state.company == null) {
            context.commit('SET_COMPANY', response.ok[0].companies[0]);
          }
          if (context.state.country == null) {
            context.commit('SET_COUNTRY', response.ok[0].country);
          }
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    APPROVE_EMAIL: async (context, email) => {
      try {
        const response = await Api.isValidEmail(email);
        context.commit('SET_EMAIL', email);
        if (response.ok) {
          context.commit('APPROVE_EMAIL', response.ok[0].approved);
          context.commit('SET_ALL_COMPANIES', response.ok[0].companies);
          context.commit('SET_IDAM_USER', response.ok[0].idam_exists);
          context.commit('SET_ROLE', response.ok[0].user_role);
          if (context.state.company == null) {
            context.commit('SET_COMPANY', response.ok[0].companies[0]);
          }
          if (context.state.country == null) {
            context.commit('SET_COUNTRY', response.ok[0].country);
          }
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    CREATE_USER_AND_IDAM: async (context, payload) => {
      try {
        const response = await Api.createUserAndIdam(
          payload.email,
          payload.organization,
          payload.user,
        );
        if (response.ok) {
          context.commit('SET_USER', response.ok[0]);
          /* eslint-disable no-constant-condition */
          if (context.state.country == null || 1) {
            context.commit('SET_COUNTRY', response.ok[0].country);
          }
          /* eslint-enable no-constant-condition */
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    EDIT_USER: async (context, payload) => {
      try {
        const response = await Api.editUser(payload.email, payload.user);
        if (response.ok) {
          context.commit('SET_USER', response.ok[0]);
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    CREATE_ADDITIONAL_INFORMATION: async (context, payload) => {
      try {
        const response = await Api.createAdditionalInformation(
          payload.email,
          payload.additionalInformation,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    EDIT_ADDITIONAL_INFORMATION: async (context, payload) => {
      try {
        const response = await Api.editAdditionalInformation(
          payload.email,
          payload.additionalInformation,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    CREATE_APPLICATION: async (context, payload) => {
      try {
        const response = await Api.createApplication(
          payload.email,
          payload.company,
          payload.application,
          payload.destinationWorkplace,
          payload.destinationResidence,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    PREVIEW_EMAILS_CSV: async (context, payload) => {
      try {
        const response = await Api.previewEmailsCsv(payload.formData);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
    ADD_BULK_EMAILS: async (context, payload) => {
      try {
        const response = await Api.addBulkEmails(payload.company_id, payload.emails);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    DELETE_BULK_EMAILS: async (context, payload) => {
      try {
        const response = await Api.deleteBulkEmails(payload.company_id, payload.emails);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_APP_USERS: async (context, companyid) => {
      try {
        const response = await Api.getAppUsers(companyid);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    ADD_APP_USER: async (context, payload) => {
      try {
        const response = await Api.addAppUser(payload.email, payload.companyId, payload.userRole);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    REMOVE_APP_USER: async (context, payload) => {
      try {
        const response = await Api.removeAppUser(payload.email, payload.companyId);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    SET_LOADING: (context, isLoading) => {
      context.commit('SET_LOADING', isLoading);
    },
    GET_UNFINISHED_APPLICATION: async (context, email) => {
      try {
        const response = await Api.getUnfinishedApplication(email);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    DELETE_UNFINISHED_APPLICATION: async (context, payload) => {
      try {
        const response = await Api.deleteUnfinishedApplication(
          payload.email,
          payload.application_id,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_COUNTRIES: async () => {
      try {
        const response = await Api.getCountries();

        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_USER_APPLICATIONS: async (context, payload) => {
      try {
        const response = await Api.getUserApplications(payload.email);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_COMPANY_APPLICATIONS: async (context, payload) => {
      try {
        const response = await Api.getCompanyApplications(payload.company_id);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_ALL_COMPANIES: async () => {
      try {
        const response = await Api.getAllCompanies();
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_COMPANY_INFO: async (context, payload) => {
      try {
        const response = await Api.getCompanyInfo(payload.id);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response.error[0] };
        }
      } catch (error) {
        return { error: error };
      }
    },
    ADD_COMPANY: async (context, payload) => {
      try {
        const response = await Api.addCompany(
          payload.name,
          payload.cvr,
          payload.max_allowed_emails,
          payload.max_allowed_applications_per_year,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
    EDIT_COMPANY: async (context, payload) => {
      try {
        const response = await Api.editCompany(
          payload.name,
          payload.cvr,
          payload.max_allowed_emails,
          payload.max_allowed_applications_per_year,
        );
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
    GET_COMPANY_LIMITS: async (context, payload) => {
      try {
        const response = await Api.getCompanyLimits(payload.company_id);
        if (response.ok) {
          return { ok: response.ok[0] };
        } else {
          return { error: response };
        }
      } catch (error) {
        return { error: error };
      }
    },
  },
  getters: {
    IS_EMAIL_APPROVED: (state) => {
      return state.isEmailApproved;
    },
    GET_LOADING_STATUS: (state) => {
      return state.isLoading;
    },
  },
  plugins: [vuexLocal.plugin],
});
